import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { TIERS, TRACK_CODE_TO_NAME } from '@backrs_ui/common-ui/src/models/careerPath';
import { TrackCode } from '@backrs_ui/common-ui/src/models/careerTrack';
import { TIER_KEY_TO_COLOR } from '@backrs_ui/common-ui/src/utils/careerTrack.util';

import { Avatar, Box, SxProps, Theme } from '@mui/material';

type HorizontalCareerTrackBarProps = Readonly<{
  currentTrack: TrackCode;
  profileImageUrl?: string;
}>;

const TRACK_ORDER = [TIERS.SEMISKILLED, TIERS.TRADE, TIERS.MID, TIERS.UPPER, TIERS.TOP] as const;

export const HorizontalCareerTrackBar = ({
  currentTrack,
  profileImageUrl,
}: HorizontalCareerTrackBarProps) => {
  const currentTrackName = TRACK_CODE_TO_NAME[currentTrack] as (typeof TRACK_ORDER)[number];
  const currentTrackIndex = TRACK_ORDER.indexOf(currentTrackName) || 0;
  const previousTrackIndex = currentTrackIndex - 1;

  return (
    <Box sx={outerContainerSx}>
      <Box sx={containerSx}>
        {TRACK_ORDER.map((track, index) => {
          const isHighlighted = index === currentTrackIndex || index === previousTrackIndex;
          const trackKey = Object.keys(TIERS).find(
            (key) => TIERS[key as keyof typeof TIERS] === track,
          ) as keyof typeof TIERS;
          const trackColor = TIER_KEY_TO_COLOR[trackKey];

          return <Box key={track} sx={segmentSx(isHighlighted, trackColor)} />;
        })}
      </Box>

      {/* Profile picture positioned at the boundary between segments */}
      <Box sx={profileImageContainerSx(currentTrackIndex)}>
        {profileImageUrl ? (
          <Avatar src={profileImageUrl} sx={profileImageSx} />
        ) : (
          <Box sx={profileImageSx}>
            <AppIcon iconName="User01" diameter="28px" color="Colors/Foreground/fg-quaternary" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const outerContainerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: '20px',
  borderRadius: '10px',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
});

const segmentSx =
  (isHighlighted: boolean, color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    flex: 1,
    height: '100%',
    backgroundColor: isHighlighted
      ? backrs.colors[color]
      : backrs.colors['Colors/Background/bg-quaternary'],
    borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    '&:last-child': {
      borderRight: 'none',
    },
  });

const profileImageContainerSx = (currentTrackIndex: number): SxProps<Theme> => ({
  position: 'absolute',
  left: `${(currentTrackIndex / TRACK_ORDER.length) * 100}%`,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
});

const profileImageSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});
